import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Buildings, X, CaretDown, DownloadSimple } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import { dmY } from "@/lib/dateFmt";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

export default function PortalStatement() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Filters (all client-side, real-time — no Apply button)
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedParties, setSelectedParties] = useState([]);
    const [showUpload, setShowUpload] = useState(true);
    const [showWithdraw, setShowWithdraw] = useState(true);
    const [partyOpen, setPartyOpen] = useState(false);
    const [partySearch, setPartySearch] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get(`/portals/${id}/statement`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    }, [id]);

    const allRows = data?.rows || [];
    const partyNames = useMemo(() => Array.from(new Set(allRows.map((r) => r.party_name).filter(Boolean))).sort(), [allRows]);

    const filtersActive = fromDate || toDate || selectedParties.length > 0 || !showUpload || !showWithdraw;

    // Filter + recompute running balance over the filtered subset (rows are date-sorted).
    const { rows, uploaded, withdrawn, balance } = useMemo(() => {
        const partySet = new Set(selectedParties);
        const out = [];
        let bal = 0, up = 0, wd = 0;
        for (const r of allRows) {
            if (fromDate && (r.date || "") < fromDate) continue;
            if (toDate && (r.date || "") > toDate) continue;
            if (partySet.size && !partySet.has(r.party_name)) continue;
            if ((r.kind === "upload" || r.kind === "receipt") && !showUpload) continue;
            if (r.kind === "withdrawal" && !showWithdraw) continue;
            if (r.kind === "upload" || r.kind === "receipt") { bal += r.amount; up += r.amount; } else { bal -= r.amount; wd += r.amount; }
            out.push({ ...r, balance: Math.round(bal * 100) / 100 });
        }
        return { rows: out, uploaded: Math.round(up * 100) / 100, withdrawn: Math.round(wd * 100) / 100, balance: Math.round(bal * 100) / 100 };
    }, [allRows, fromDate, toDate, selectedParties, showUpload, showWithdraw]);

    const clearFilters = () => { setFromDate(""); setToDate(""); setSelectedParties([]); setShowUpload(true); setShowWithdraw(true); setPartySearch(""); };
    const toggleParty = (name) => setSelectedParties((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);

    const downloadExport = async (fmt) => {
        try {
            const res = await api.get(`/export/portal-statement/${id}.${fmt}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers["content-disposition"] || "";
            const mm = cd.match(/filename="?([^"]+)"?/);
            a.download = mm ? mm[1] : `portal-statement.${fmt}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="portal-statement-page">
            <div>
                <Link to="/admin/portals" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                    <ArrowLeft size={12} /> Back to Portals
                </Link>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md"><Buildings size={20} weight="fill" /></div>
                    <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">{data?.portal?.name || "Portal"} Statement</h1>
                    <div className="ml-auto flex items-center gap-2">
                        <button type="button" data-testid="portal-statement-export-xlsx" onClick={() => downloadExport("xlsx")} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"><DownloadSimple size={14} weight="bold" /> Excel</button>
                        <button type="button" data-testid="portal-statement-export-pdf" onClick={() => downloadExport("pdf")} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"><DownloadSimple size={14} weight="bold" /> PDF</button>
                    </div>
                </div>
            </div>

            {data && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4"><div className="text-[10px] uppercase tracking-[0.18em] text-emerald-800">Uploaded</div><div data-testid="portal-statement-uploaded" className="mt-1 font-display text-xl font-bold text-emerald-800">{fmt(uploaded)}</div></div>
                    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-4"><div className="text-[10px] uppercase tracking-[0.18em] text-rose-800">Withdrawn</div><div data-testid="portal-statement-withdrawn" className="mt-1 font-display text-xl font-bold text-rose-700">{fmt(withdrawn)}</div></div>
                    <div className="rounded-lg border border-slate-200 bg-white p-4"><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Balance</div><div data-testid="portal-statement-balance" className={`mt-1 font-display text-xl font-bold ${balance < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmt(balance)}</div></div>
                </div>
            )}

            {/* Filters */}
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="portal-statement-filters">
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500">From</label>
                        <input type="date" data-testid="psf-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 h-9 rounded-md border border-slate-200 px-2 text-sm" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500">To</label>
                        <input type="date" data-testid="psf-to" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 h-9 rounded-md border border-slate-200 px-2 text-sm" />
                    </div>
                    <div className="relative">
                        <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500">Parties</label>
                        <button type="button" data-testid="psf-party-trigger" onClick={() => setPartyOpen((o) => !o)} className="mt-1 inline-flex h-9 min-w-[140px] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 text-sm hover:bg-slate-50">
                            <span>{selectedParties.length ? `${selectedParties.length} selected` : "All parties"}</span>
                            <CaretDown size={13} />
                        </button>
                        {partyOpen && (
                            <div className="absolute z-20 mt-1 max-h-72 w-64 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg" data-testid="psf-party-panel">
                                <input autoFocus value={partySearch} onChange={(e) => setPartySearch(e.target.value)} placeholder="Search party…" className="w-full border-b border-slate-100 px-3 py-2 text-sm outline-none" />
                                <div className="max-h-56 overflow-y-auto p-1">
                                    {partyNames.filter((n) => n.toLowerCase().includes(partySearch.toLowerCase())).map((n) => (
                                        <label key={n} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                                            <input type="checkbox" checked={selectedParties.includes(n)} onChange={() => toggleParty(n)} data-testid={`psf-party-${n}`} />
                                            <span className="truncate">{n}</span>
                                        </label>
                                    ))}
                                    {partyNames.length === 0 && <div className="px-2 py-2 text-xs text-slate-400">No parties</div>}
                                </div>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.15em] text-slate-500">Type</label>
                        <div className="mt-1 flex items-center gap-3 text-sm">
                            <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="psf-type-upload" checked={showUpload} onChange={(e) => setShowUpload(e.target.checked)} /> Upload</label>
                            <label className="flex items-center gap-1.5"><input type="checkbox" data-testid="psf-type-withdraw" checked={showWithdraw} onChange={(e) => setShowWithdraw(e.target.checked)} /> Withdraw</label>
                        </div>
                    </div>
                    {filtersActive && (
                        <button type="button" onClick={clearFilters} data-testid="psf-clear" className="inline-flex h-9 items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-3 text-sm font-semibold text-rose-700 hover:bg-rose-100">
                            <X size={13} /> Clear Filters
                        </button>
                    )}
                </div>
                {selectedParties.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {selectedParties.map((n) => (
                            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700">
                                {n}<button onClick={() => toggleParty(n)} className="hover:text-indigo-900"><X size={11} /></button>
                            </span>
                        ))}
                    </div>
                )}
                <div className="mt-2 text-xs text-slate-500" data-testid="portal-statement-count">Showing {rows.length} of {allRows.length} transactions</div>
            </div>

            {rows.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"><EmptyState icon={Buildings} title="No transactions" description={filtersActive ? "No transactions match the selected filters." : "No verified slips or withdrawals are linked to this portal yet."} /></div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm" data-testid="portal-statement-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Date</th>
                                <th className="px-4 py-3 text-left">Type</th>
                                <th className="px-4 py-3 text-left">Party</th>
                                <th className="px-4 py-3 text-left">Detail / Ref</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3 text-right">Balance</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={i} data-testid={`portal-statement-row-${i}`} className="hover:bg-slate-50">
                                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-slate-700">{r.date ? dmY(r.date) : "—"}</td>
                                    <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.kind === "withdrawal" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-800"}`}>{r.kind === "upload" ? "Upload" : r.kind === "receipt" ? "Receipt" : "Withdrawal"}</span></td>
                                    <td className="px-4 py-2 text-slate-700">{r.party_name || "—"}</td>
                                    <td className="px-4 py-2 text-xs text-slate-600">{r.detail}{r.ref ? ` · ${r.ref}` : ""}</td>
                                    <td className={`px-4 py-2 text-right font-mono font-semibold tabular-nums ${r.kind === "withdrawal" ? "text-rose-600" : "text-emerald-700"}`}>{r.kind === "withdrawal" ? "−" : "+"}{fmt(r.amount)}</td>
                                    <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-900">{fmt(r.balance)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
